import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

/** App mode: wallet (use mints) or mint (operate a mint) */
export type AppMode = 'wallet' | 'mint';

export interface ModeContextType {
  /** Current mode */
  mode: AppMode;
  /** Switch between wallet and mint modes */
  setMode: (mode: AppMode) => void;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export function ModeProvider({ children }: { children: ReactNode }) {
  const [storedMode, setStoredMode] = useLocalStorage<AppMode>('cashero:mode', 'wallet');
  const [mode, setModeState] = useState<AppMode>(storedMode ?? 'wallet');

  const setMode = useCallback((newMode: AppMode) => {
    setModeState(newMode);
    setStoredMode(newMode);
  }, [setStoredMode]);

  return (
    <ModeContext.Provider value={{ mode, setMode }}>
      {children}
    </ModeContext.Provider>
  );
}

export function useAppMode(): ModeContextType {
  const context = useContext(ModeContext);
  if (!context) throw new Error('useAppMode must be used within ModeProvider');
  return context;
}
